import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Trans, useTranslation } from 'react-i18next';
import { Field, useFormikContext } from 'formik';

import INTRODUCTION_PANEL from 'assets/images/introduction-panel.webp';
import { FormSection } from 'common/components/FormSection';
import { PAGE_IMAGE_MAX_SIZE, PAGE_IMAGE_TYPE } from 'common/constants/files';
import { EImageVariant } from 'common/types';
import {
    ColorPicker,
    ImageField,
    Switch,
    TextField,
} from 'common/components/FormField';
import { Banner } from 'common/components/Banner';
import { Img } from 'common/components/Img';
import { Link } from 'common/components/Link';
import { IMAGE_VARIANTS } from 'common/constants/image';

import { TOrganisationSettingsFormValues } from './OrganisationSettingsForm';

export const IntroductionPanelForm = () => {
    const [translate] = useTranslation();
    const smallScreen = useMediaQuery((theme: Theme) =>
        theme.breakpoints.only('xs')
    );
    const { values } = useFormikContext<TOrganisationSettingsFormValues>();

    return (
        <FormSection
            description={
                <Box>
                    <Trans
                        components={{
                            a: <Link rel="noreferrer" underline="always" />,
                        }}
                        i18nKey="academySettingsForm.introductionPanelDescription"
                    />
                </Box>
            }
            title={translate('academySettingsForm.introductionPanelTitle')}
        >
            <Stack
                direction={smallScreen ? 'column-reverse' : 'row'}
                spacing={2}
                sx={{ mt: 2 }}
            >
                <Box sx={{ width: '100%' }}>
                    <Field
                        acceptedFileTypes={PAGE_IMAGE_TYPE}
                        component={ImageField}
                        helperText={translate('imageSizeDescription', {
                            width: IMAGE_VARIANTS[EImageVariant.DrawerHeader]
                                .minWidth,
                            height: IMAGE_VARIANTS[EImageVariant.DrawerHeader]
                                .minHeight,
                        })}
                        imageVariant={EImageVariant.DrawerHeader}
                        label={translate(
                            'academySettingsForm.introductionPanelImage.label'
                        )}
                        maxUploadSize={PAGE_IMAGE_MAX_SIZE}
                        name="homeHeaderImage"
                    />
                </Box>

                <Box sx={{ width: '100%' }}>
                    <Banner>
                        <Box sx={{ py: 1, px: 2 }}>
                            {translate(
                                'academySettingsForm.introductionPanelImage.description'
                            )}
                        </Box>
                        <Box sx={{ mt: 'auto' }}>
                            <Img src={INTRODUCTION_PANEL} />
                        </Box>
                    </Banner>
                </Box>
            </Stack>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size="grow">
                    <Field
                        component={TextField}
                        label={translate('title')}
                        name="homeTitle"
                    />
                </Grid>
                <Grid>
                    <Field
                        component={ColorPicker}
                        name="homeTitleColor"
                        popoverPosition="bottom-end"
                        tooltip={translate('titleColor')}
                    />
                </Grid>
            </Grid>
            <Field
                multiline
                component={TextField}
                label={translate('description')}
                name="homeText"
            />
            <Box sx={{ mb: -2 }}>
                <Field
                    checked={values.showIntroductionPanel}
                    component={Switch}
                    label={translate(
                        'academySettingsForm.introductionPanelPublish'
                    )}
                    name="showIntroductionPanel"
                />
            </Box>
        </FormSection>
    );
};
